import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

import { Header } from '../Header';
import { PostLoader } from '../PostLoader';
import { Notification } from '../Notification';
import { checkUser, getRedirectResult } from '../../helpers/magic';
import { UserContext } from '../../context/userContext';
import { isIos, canShare } from '../../helpers/shared';
import { StrokeIcon } from '../../Icons';
import { PriceContext } from '../../context/priceContext';

type Props = {
  children: React.ReactChild;
};

type User = {
  email: string;
  id: string;
  hasPremium: boolean;
  subscriptionEnd: number | null;
  isNewSubscriber: boolean;
  isCanceled: boolean | null;
  plan: {
    amount: number;
    interval: string;
    isTrial: boolean;
  } | null;
  subscriptionAfterTrail: boolean;
};

type Price = {
  monthly: {
    amount: number;
    price: string;
  };
  annually: {
    amount: number;
    price: string;
  };
  trial: {
    amount: number;
    price: string;
  };
};

const Layout = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [price, setPrice] = useState<Price | null>(null);
  const [isPostLoaderVisible, setPostLoaderVisible] = useState(false);
  const [isWarningVisible, setWarningVisible] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const getProducts = async () => {
      const data = await fetch('/.netlify/functions/get-products')
        .then((res) => res.json())
        // eslint-disable-next-line no-console
        .catch((err) => console.error(err));

      return data;
    };

    getProducts().then((r) => {
      const monthly = r.find((item: any) => item.id === process.env.REACT_APP_STRIPE_MONTHLY);
      const annually = r.find((item: any) => item.id === process.env.REACT_APP_STRIPE_ANNUALLY);
      const trial = r.find((item: any) => item.id === process.env.REACT_APP_STRIPE_TRIAL);

      setPrice({
        monthly: {
          amount: monthly.amount,
          price: monthly.price,
        },
        annually: {
          amount: annually.amount,
          price: annually.price,
        },
        trial: {
          amount: trial.amount,
          price: trial.price,
        },
      });
    });
  }, []);

  useEffect(() => {
    if (window.location.pathname.includes('redirect') && !user) {
      getRedirectResult(setUser);
    }

    const validateUser = async () => {
      try {
        await checkUser(setUser);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    };
    validateUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.location.pathname.includes('redirect') && !user) {
      setPostLoaderVisible(true);
    } else if (user) {
      const path = /\/redirect.*/;
      setPostLoaderVisible(false);
      navigate(window.location.pathname.replace(path, ''));
    }
  }, [setPostLoaderVisible, user, navigate]);

  useEffect(() => {
    if (isIos() && !canShare()) {
      setWarningVisible(true);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname.includes('/subscription-activated')) {
      const path = /\/subscription-activated.*/;
      navigate(window.location.pathname.replace(path, ''));
      mixpanel.track('Subscription activated', { premium: true, trial: false });
    } else if (window.location.pathname.includes('/trial-activated')) {
      const path = /\/trial-activated.*/;
      navigate(window.location.pathname.replace(path, ''));
      mixpanel.track('Trial activated', { premium: true, trial: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (isWarningVisible) {
      const timer = setTimeout(() => setWarningVisible(false), 4000);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  useEffect(() => {
    if (user && user.subscriptionAfterTrail && user.email) {
      const isSent = localStorage.getItem(`${user.email}_after_trial`);
      if (!isSent) {
        localStorage.setItem(`${user.email}_after_trial`, 'true');
        mixpanel.track('Subscription activated', { premium: true, trial: false });
      }
    }
  }, [user]);

  const value = useMemo(() => ({ user, setUser }), [user, setUser]);
  const priceValue = useMemo(() => ({ price, setPrice }), [price, setPrice]);

  return (
    <div className='w-full'>
      <UserContext.Provider value={value}>
        <PriceContext.Provider value={priceValue}>
          <Header />
          {children}
          <PostLoader isOpen={isPostLoaderVisible} onClose={() => setPostLoaderVisible(true)} />
          {isWarningVisible && (
            <Notification
              icon={<StrokeIcon className='w-[20px]' />}
              text='Content install is working only on iOS 15 and newer'
            />
          )}
        </PriceContext.Provider>
      </UserContext.Provider>
    </div>
  );
};

export { Layout };
